import { useSyncExternalStore } from "react";

const MINUTE_MS = 60_000;

// One ticker for the whole app, however many components read the clock. It
// runs only while something is subscribed and fires on each minute boundary.
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;

function notify() {
  for (const listener of listeners) listener();
}

function scheduleTick() {
  timer = setTimeout(
    () => {
      notify();
      scheduleTick();
    },
    MINUTE_MS - (Date.now() % MINUTE_MS),
  );
}

// A backgrounded tab throttles timers, so catch up as soon as it is visible.
function onVisibilityChange() {
  if (document.visibilityState === "visible") notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) {
    scheduleTick();
    document.addEventListener("visibilitychange", onVisibilityChange);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      if (timer) clearTimeout(timer);
      timer = null;
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
  };
}

/** Floored to the minute so repeated reads within a minute are identical. */
function getSnapshot(): number {
  return Math.floor(Date.now() / MINUTE_MS) * MINUTE_MS;
}

/**
 * The current time in epoch milliseconds, to the minute. Re-renders the
 * caller when the minute changes.
 */
export function useNow(): number {
  return useSyncExternalStore(subscribe, getSnapshot);
}
